import type { VariantProps } from 'class-variance-authority'

import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'

import { buttonVariants } from '@/components/ui/button-variants'
import { cn } from '@/lib/utils'

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }
>((
  {
    className,
    variant,
    size,
    asChild = false,
    ...props
  },
  ref,
) => {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  )
})

Button.displayName = 'Button'

interface ButtonGroupProps {
  className?: string
  orientation?: 'horizontal' | 'vertical'
  children: React.ReactNode
}

function ButtonGroup({
  className,
  orientation = 'horizontal',
  children,
}: ButtonGroupProps) {
  const isHorizontal = orientation === 'horizontal'
  const isVertical = orientation === 'vertical'

  return (
    <div
      className={cn(
        'flex',
        isHorizontal
        && '[&>*:not(:first-child)]:rounded-l-none [&>*:not(:last-child)]:rounded-r-none [&>*:not(:first-child)]:border-l-0',
        {
          'flex-col': isVertical,
          'w-fit': isVertical,
        },
        isVertical
        && '[&>*:not(:first-child)]:rounded-t-none [&>*:not(:last-child)]:rounded-b-none [&>*:not(:first-child)]:border-t-0',
        className,
      )}
    >
      {children}
    </div>
  )
}

ButtonGroup.displayName = 'ButtonGroup'

export { Button, ButtonGroup }
